import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useGlobalStore } from '../../stores/globalStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { useReportViewer } from '../../contexts/ReportViewerContext';
import { fetchReportDocuments, fetchReportStatuses, getReportPdfBlob } from '../../api/reportsApi';
import type { ReportDocumentParams } from '../../types/reports';
import { canPrintPdfInPage, printPdfBlob } from '../../utils/printPdf';
import { AUTO_OPEN_POLL_MS, outcomeForStatus } from './reportAutoOpenerRules';
import { markReportPrinted, markReportViewed } from './readyReports';

export interface WatchOptions {
  /**
   * The user asked to print, not to look: when the document is ready, open the
   * browser's print dialog on its PDF instead of the viewer. Falls back to the
   * viewer where a page cannot print a PDF (phones, tablets) or the print fails.
   */
  print?: boolean;
}

interface ReportAutoOpenerContextValue {
  /** Queue a report document (by its `PKey_RS_ReportDocument`) for
   *  auto-open once its status flips to Completed. `reportName` is
   *  used for the notification if it terminates in NO_DATA / ERROR. */
  watchAndOpen: (reportDocumentId: number, reportName: string, options?: WatchOptions) => void;
  /** Live set of watched pKeys. `useReportCompletionNotifier` reads
   *  this to suppress its passive "X is ready" toast for docs that
   *  are already being auto-handled here. */
  watchedIds: Set<number>;
}

const Ctx = createContext<ReportAutoOpenerContextValue | null>(null);

interface StatusRow {
  id: number;
  status: number;
  guid: string | null;
  message: string | null;
}

function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 404;
}

/**
 * App-root provider that watches user-submitted "on demand" report
 * documents (e.g. the Residents grid's Print sub-menu) and pops the
 * global report viewer as soon as each one finishes generating, or,
 * for a print request, opens the print dialog on it.
 *
 * Complements `useReportCompletionNotifier`, which handles the
 * passive/queued case with a toast. Both hooks are safe to mount
 * simultaneously: the notifier consults `watchedIds` here and skips
 * any pKey that this hook is already handling.
 *
 * Polling only runs while `watched` is non-empty, and stops as soon
 * as the last watched doc reaches a terminal state, so there is no idle traffic.
 */
export function ReportAutoOpenerProvider({ children }: { children: ReactNode }) {
  const facilityId = useGlobalStore((s) => s.facilityId);
  const userId = useGlobalStore((s) => s.userId);
  const { openReport } = useReportViewer();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const dismissNotification = useNotificationStore((s) => s.dismissNotification);

  // pKey → entry. State (not ref) so the polling effect re-triggers when a
  // new pKey is added. The progress toast id is the persistent "Generating"
  // spinner raised the moment a doc is registered, dismissed the instant the
  // doc reaches any terminal state.
  interface WatchedEntry { name: string; progressToastId: string | null; print: boolean }
  const [watched, setWatched] = useState<Map<number, WatchedEntry>>(() => new Map());
  // Same keys, updated synchronously, so a double click can't raise two toasts
  // for one document before the state update lands.
  const watchedKeysRef = useRef<Set<number>>(new Set());
  // Set once the API answers 404 to the status call (a React build ahead of
  // its API): from then on, find the documents in the print queue as before.
  const useQueueFallbackRef = useRef(false);

  // Latest values, so the polling closure doesn't capture stale callbacks.
  // Synced after render rather than during it.
  const openReportRef = useRef(openReport);
  const notifyRef = useRef(addNotification);
  const dismissRef = useRef(dismissNotification);
  useEffect(() => {
    openReportRef.current = openReport;
    notifyRef.current = addNotification;
    dismissRef.current = dismissNotification;
  }, [openReport, addNotification, dismissNotification]);

  useEffect(() => {
    if (watched.size === 0 || !facilityId || !userId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const readStatuses = async (ids: number[]): Promise<StatusRow[]> => {
      if (!useQueueFallbackRef.current) {
        try {
          const rows = await fetchReportStatuses(facilityId, ids);
          return rows.map((r) => ({ id: r.pKey_RS_ReportDocument, status: r.status, guid: r.guid, message: r.statusMessage }));
        } catch (e) {
          if (!isNotFound(e)) throw e;
          useQueueFallbackRef.current = true;
        }
      }
      const params: ReportDocumentParams = {
        viewAll: false,
        testingMode: false,
        viewDeletedReports: false,
        viewTickets: false,
        pKeysManualProcess: [],
        fKey_User: userId,
      };
      const docs = await fetchReportDocuments(facilityId, params);
      return docs
        .filter((d) => ids.includes(d.pKey_RS_ReportDocument))
        .map((d) => ({ id: d.pKey_RS_ReportDocument, status: d.status, guid: d.guid, message: d.statusMessage }));
    };

    // Opened on screen: Viewed. Printed comes from the viewer's Print button.
    const view = (id: number, guid: string, name: string) => {
      void markReportViewed(facilityId, id, userId);
      openReportRef.current(guid, name, id);
    };

    const printOrOpen = (id: number, guid: string, name: string) => {
      getReportPdfBlob(facilityId, guid)
        .then((pdf) => printPdfBlob(pdf))
        .then(() => {
          void markReportPrinted(facilityId, id, userId);
          notifyRef.current({
            message: `Printing "${name}"`,
            type: 'success',
            duration: 8000,
            action: { label: 'View', handler: () => openReportRef.current(guid, name, id) },
          });
        })
        .catch(() => {
          // The PDF wouldn't download or print in the page: the viewer's own
          // Print button still works.
          view(id, guid, name);
        });
    };

    const poll = async () => {
      if (cancelled) return;
      const ids = [...watched.keys()];
      try {
        const rows = await readStatuses(ids);
        if (cancelled) return;
        const done: number[] = [];
        for (const row of rows) {
          const entry = watched.get(row.id);
          if (!entry) continue;
          const outcome = outcomeForStatus(row.status, row.guid);
          if (outcome === 'wait') continue;

          // Any terminal status: the spinner toast never outlives the flow.
          if (entry.progressToastId) dismissRef.current(entry.progressToastId);
          done.push(row.id);

          if (outcome === 'open' && row.guid) {
            // Printed or opened for the user: marked Printed or Viewed, or it
            // would sit in the sidebar's ready count.
            if (entry.print) printOrOpen(row.id, row.guid, entry.name);
            else view(row.id, row.guid, entry.name);
          } else if (outcome === 'noData') {
            notifyRef.current({ message: `"${entry.name}" completed with no data`, type: 'info', duration: 8000 });
          } else if (outcome === 'error') {
            notifyRef.current({ message: `"${entry.name}" failed to generate`, type: 'error', duration: 10000 });
          } else if (outcome === 'info') {
            notifyRef.current({
              message: row.message ? `"${entry.name}": ${row.message}` : `"${entry.name}" needs more information`,
              type: 'info',
              duration: 10000,
            });
          }
          // canceled: silent. The user (or the server auto-cancel) already knows.
        }
        if (done.length) {
          for (const id of done) watchedKeysRef.current.delete(id);
          setWatched((prev) => {
            const next = new Map(prev);
            for (const id of done) next.delete(id);
            return next;
          });
          return; // the state change restarts polling for whatever is left
        }
      } catch {
        // Transient poll failure: swallow and let the next tick try
        // again. A hard-down API surfaces separately via ApiDownOverlay.
      }
      if (!cancelled) timer = setTimeout(poll, AUTO_OPEN_POLL_MS);
    };

    // Check once straight away (a re-run after another doc finished, or a
    // render that beat us), then every AUTO_OPEN_POLL_MS. Chained timeouts,
    // not an interval, so a slow answer never overlaps the next request.
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [watched, facilityId, userId]);

  const watchAndOpen = useCallback((reportDocumentId: number, reportName: string, options?: WatchOptions) => {
    if (watchedKeysRef.current.has(reportDocumentId)) return;
    watchedKeysRef.current.add(reportDocumentId);
    // Decided now, so the banner promises what will actually open.
    const print = !!options?.print && canPrintPdfInPage();
    // Persistent spinner toast from registration until a terminal status.
    // Raised here, outside the state updater: an updater must stay pure, and
    // notifying from inside one updated the toast host while this provider
    // rendered (React's "Cannot update a component while rendering a
    // different component").
    const progressToastId = notifyRef.current({
      message: print
        ? `Preparing "${reportName}" for printing - the print dialog will open when it is ready`
        : `Generating "${reportName}" - the viewer will open when ready`,
      type: 'info',
      duration: 0, // 0 → persistent (see notificationStore.ts)
      showSpinner: true,
    });
    setWatched((prev) => {
      const next = new Map(prev);
      next.set(reportDocumentId, { name: reportName, progressToastId, print });
      return next;
    });
  }, []);

  const watchedIds = useMemo(() => new Set(watched.keys()), [watched]);

  const value = useMemo<ReportAutoOpenerContextValue>(
    () => ({ watchAndOpen, watchedIds }),
    [watchAndOpen, watchedIds],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Read the auto-opener's `watchAndOpen` action. Throws if used
 * outside `<ReportAutoOpenerProvider>` so a missing provider surfaces
 * loud instead of silently no-op-ing.
 */
/** The same, or null outside the provider: for a page that can still queue without opening (it says where the report went). */
// eslint-disable-next-line react-refresh/only-export-components -- a hook beside its provider, like useReportAutoOpener
export function useOptionalReportAutoOpener(): ReportAutoOpenerContextValue | null {
  return useContext(Ctx);
}

export function useReportAutoOpener(): ReportAutoOpenerContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error('useReportAutoOpener must be used inside <ReportAutoOpenerProvider>');
  }
  return ctx;
}
